import { useEffect, useState } from "react";
import { KeyRound, Loader2, Plus, Trash2, UserCheck, UserX } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { ROUTES } from "@/lib/routes";

const err = (e, fallback) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : fallback);

export function StaffAdmin() {
  const [list, setList] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/staff").then((r) => setList(r.data.staff)).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/staff", form);
      toast.success(`Dodano konto pracownika ${form.email}.`);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (er) { err(er, "Nie udało się dodać pracownika."); } finally { setBusy(false); }
  };
  const toggle = async (s) => {
    try { await api.patch(`/admin/staff/${s.id}`, { active: !s.active }); load(); } catch (er) { err(er, "Błąd zapisu."); }
  };
  const resetPw = async (s) => {
    const pw = window.prompt(`Nowe hasło dla ${s.email} (min. 8 znaków, litery i cyfry):`);
    if (!pw) return;
    try { await api.patch(`/admin/staff/${s.id}`, { password: pw }); toast.success("Hasło zmienione."); } catch (er) { err(er, "Nie udało się zmienić hasła."); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Usunąć konto ${s.email}?`)) return;
    try { await api.delete(`/admin/staff/${s.id}`); toast.success("Konto usunięte."); load(); } catch (er) { err(er, "Błąd usuwania."); }
  };
  const url = `${window.location.origin}${ROUTES.staff}`;

  return (
    <div data-testid="staff-admin" className="space-y-6">
      <div className="bg-card border border-border rounded-2xl p-6">
        <h3 className="font-display font-bold text-lg">Konta pracowników</h3>
        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
          Pracownicy logują się (bez dostępu do panelu) na stronie <a data-testid="staff-portal-link" href={url} target="_blank" rel="noreferrer" className="text-brand-teal font-semibold underline">{url}</a> i generują dokumenty oznaczone jako „wewnętrzne” w zakładce Generator dokumentów. Sesja trwa 8 godzin.
        </p>
        <form onSubmit={create} className="mt-5 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <div><label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Imię i nazwisko</label><Input data-testid="staff-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
          <div><label className="text-xs font-semibold text-muted-foreground mb-1.5 block">E-mail (login)</label><Input data-testid="staff-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
          <div><label className="text-xs font-semibold text-muted-foreground mb-1.5 block">Hasło</label><Input data-testid="staff-password-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="min. 8 znaków, litery i cyfry" required /></div>
          <button type="submit" data-testid="staff-create-button" disabled={busy} className="inline-flex items-center justify-center gap-2 bg-brand-teal hover:bg-brand-tealDark disabled:opacity-60 text-white font-semibold px-5 py-2.5 rounded-full text-sm transition-colors">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Dodaj pracownika
          </button>
        </form>
      </div>
      <div className="bg-card border border-border rounded-2xl divide-y divide-border">
        {!list && <div className="p-6 flex justify-center"><Loader2 className="animate-spin text-slate-500" /></div>}
        {list && !list.length && <p data-testid="staff-empty" className="p-6 text-sm text-muted-foreground">Brak kont pracowników.</p>}
        {(list || []).map((s) => (
          <div key={s.id} data-testid={`staff-row-${s.id}`} className="p-4 sm:px-6 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px]">
              <p className="font-semibold text-sm">{s.name} {!s.active && <span className="ml-2 font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-brand-red/10 text-brand-red">zablokowany</span>}</p>
              <p className="text-xs text-muted-foreground">{s.email}{s.last_login_at ? ` · ostatnie logowanie ${new Date(s.last_login_at).toLocaleString("pl-PL")}` : " · jeszcze się nie logował"}</p>
            </div>
            <button data-testid={`staff-toggle-${s.id}`} onClick={() => toggle(s)} className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-full px-3 py-1.5 hover:bg-muted">{s.active ? <><UserX size={13} /> Zablokuj</> : <><UserCheck size={13} /> Odblokuj</>}</button>
            <button data-testid={`staff-password-${s.id}`} onClick={() => resetPw(s)} className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-full px-3 py-1.5 hover:bg-muted"><KeyRound size={13} /> Zmień hasło</button>
            <button data-testid={`staff-delete-${s.id}`} onClick={() => remove(s)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-red border border-brand-red/30 rounded-full px-3 py-1.5 hover:bg-brand-red/10"><Trash2 size={13} /> Usuń</button>
          </div>
        ))}
      </div>
    </div>
  );
}
